import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { mentorshipsAPI, hubError } from '../lib/api';
import { TopBar } from '../components/layout/TopBar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Sprout, Loader2, Users, CalendarDays, Square } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination } from '../components/Pagination';
import { usePagination } from '../hooks/usePagination';

const statusMap = {
  pending: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-700' },
  active: { label: 'Activa', color: 'bg-green-100 text-green-700' },
  accepted: { label: 'Activa', color: 'bg-green-100 text-green-700' },
  ended: { label: 'Finalizada', color: 'bg-gray-100 text-gray-700' },
  rejected: { label: 'Rechazada', color: 'bg-red-100 text-red-700' },
};

export default function MentorshipsPage() {
  const { canUpdate } = useAuth();
  const { setMobileMenuOpen, churchInfo } = useOutletContext();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const pg = usePagination(status);

  const fetchItems = useCallback(() => {
    setLoading(true);
    mentorshipsAPI.list(status === 'all' ? undefined : status, pg.params)
      .then(res => { setItems(res.data?.data || []); setTotal(res.data?.total || 0); })
      .catch(err => toast.error(hubError(err, 'Error al cargar mentorías')))
      .finally(() => setLoading(false));
  }, [status, pg.params]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const endMentorship = async (id) => {
    try {
      await mentorshipsAPI.end(id);
      toast.success('Mentoría finalizada');
      fetchItems();
    } catch (err) { toast.error(hubError(err, 'Error al finalizar la mentoría')); }
  };

  return (
    <div>
      <TopBar title="Mentorías" onMenuClick={() => setMobileMenuOpen(true)} churchInfo={churchInfo} />
      <div className="px-4 sm:px-6 lg:px-8 py-5">
        <Tabs value={status} onValueChange={setStatus} className="mb-4">
          <TabsList>
            <TabsTrigger value="all" data-testid="mentorships-filter-all">Todas</TabsTrigger>
            <TabsTrigger value="pending" data-testid="mentorships-filter-pending">Pendientes</TabsTrigger>
            <TabsTrigger value="active" data-testid="mentorships-filter-active">Activas</TabsTrigger>
            <TabsTrigger value="ended" data-testid="mentorships-filter-ended">Finalizadas</TabsTrigger>
          </TabsList>
        </Tabs>

        <Pagination
          total={total} page={pg.page} pageSize={pg.pageSize}
          onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize}
          label="mentorías" testId="mentorships-pagination"
        />

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 spinner text-[#056A84]" /></div>
        ) : items.length === 0 ? (
          <div className="empty-state"><Sprout className="w-12 h-12 text-muted-foreground/40" /><p>No hay mentorías en esta categoría</p></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {items.map(item => {
              const si = statusMap[item.status] || statusMap.pending;
              const isEnded = item.status === 'ended' || !!item.ended_at;
              return (
                <Card key={item.id} className="stat-card" data-testid={`mentorship-card-${item.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-xl bg-[#1F9D7A]/10 flex items-center justify-center">
                        <Sprout className="w-5 h-5 text-[#1F9D7A]" />
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${si.color}`}>{si.label}</span>
                    </div>
                    <div className="space-y-1.5 text-sm">
                      <p className="flex items-center gap-2"><Users className="w-3.5 h-3.5 text-muted-foreground" /> Mentor: <span className="font-medium">{item.mentor_name || item.mentor_user_id}</span></p>
                      <p className="flex items-center gap-2"><Users className="w-3.5 h-3.5 text-muted-foreground" /> Discípulo: <span className="font-medium">{item.mentee_name || item.mentee_user_id}</span></p>
                      {item.request_message && <p className="text-xs text-muted-foreground mt-2">"{item.request_message}"</p>}
                      {item.response_note && <Badge variant="secondary" className="text-[10px]">{item.response_note}</Badge>}
                    </div>
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-border/50">
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <CalendarDays className="w-3 h-3" />
                        {item.created_at ? new Date(item.created_at).toLocaleDateString('es-ES') : ''}
                      </span>
                      {canUpdate('mentorships') && !isEnded && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" className="text-xs" data-testid={`mentorship-end-${item.id}`}>
                              <Square className="w-3 h-3 mr-1" /> Finalizar
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Finalizar esta mentoría?</AlertDialogTitle>
                              <AlertDialogDescription>El acompañamiento se cerrará de forma supervisada.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => endMentorship(item.id)} className="bg-[#056A84]">Finalizar</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
